package org.teddapp.project.parcours

/**
 * La fiche d'un parcours rendue en Markdown, pour en faire une ressource texte de la
 * compétence : l'assistant la lit alors comme un document, module par module,
 * objectifs, résultats attendus, feuilles, projets, livrables et critères
 * (contrat assistant et parcours, section 2).
 *
 * Rien n'est inventé : chaque ligne vient du fichier importé. L'état des cases
 * n'y figure pas, il change tout le temps ; c'est le résumé envoyé avec la
 * question qui le porte.
 */
object ParcoursMarkdown {
    /** Longueur enregistrable d'un texte, règle du serveur ; au-delà, la fiche est coupée. */
    const val BODY_MAX = 100_000

    /** Le titre de la ressource d'une compétence. */
    fun title(plan: ParcoursPlan): String = "Parcours : ${plan.competence.trim()}"

    fun render(plan: ParcoursPlan): String {
        val out = StringBuilder()
        out.append("# Parcours : ").append(plan.competence.trim()).append("\n\n")
        out.append(
            "Formation en ${plan.modules.size} module(s) et ${plan.projects.size} projet(s), " +
                "${minutes(plan.totalMinutes)} au total : ${minutes(plan.socleMinutes)} de socle, ${minutes(plan.projectMinutes)} de projets.\n\n",
        )
        for (step in plan.steps) {
            when (step) {
                is ParcoursModulePlan -> module(out, step)
                is ParcoursProjectPlan -> project(out, step)
            }
        }
        val text = out.toString().trimEnd() + "\n"
        return if (text.length <= BODY_MAX) text else text.take(BODY_MAX - 40).trimEnd() + "\n\n[fiche coupée]\n"
    }

    private fun module(out: StringBuilder, step: ParcoursModulePlan) {
        out.append("## ").append(step.code).append(" ").append(step.title).append(" (module, ").append(minutes(step.minutes)).append(")\n\n")
        step.summary?.takeIf { it.isNotBlank() }?.let { out.append(it.trim()).append("\n\n") }
        for (obj in step.objectives) {
            out.append("### ").append(obj.code).append(" ").append(obj.title).append("\n\n")
            obj.outcome?.takeIf { it.isNotBlank() }?.let { out.append("Résultat attendu : ").append(it.trim()).append("\n\n") }
            for (task in obj.tasks) {
                out.append("- ").append(task.code).append(" ").append(task.title).append(" (").append(minutes(task.minutes)).append(")\n")
                for (micro in task.micros) {
                    out.append("  - ").append(micro.title).append(" (").append(micro.minutes).append(" min")
                    PARCOURS_KINDS[micro.kind]?.let { out.append(", ").append(it.lowercase()) }
                    out.append(")\n")
                }
            }
            out.append("\n")
        }
    }

    private fun project(out: StringBuilder, step: ParcoursProjectPlan) {
        out.append("## ").append(step.code).append(" ").append(step.title).append(" (projet, ").append(minutes(step.minutes)).append(")\n\n")
        step.context?.takeIf { it.isNotBlank() }?.let { out.append(it.trim()).append("\n\n") }
        step.levelName?.takeIf { it.isNotBlank() }?.let {
            out.append("Palier atteint : ").append(it.trim())
            step.levelDesc?.takeIf { d -> d.isNotBlank() }?.let { d -> out.append(" — ").append(d.trim()) }
            out.append("\n\n")
        }
        if (step.deliverables.isNotEmpty()) {
            out.append("Livrables :\n")
            for (d in step.deliverables) out.append("- ").append(d.trim()).append("\n")
            out.append("\n")
        }
        if (step.actions.isNotEmpty()) {
            out.append("Actions :\n")
            for (a in step.actions) {
                out.append("- ").append(a.code).append(" ").append(a.title).append(" (").append(minutes(a.minutes)).append(")")
                a.deliverable?.takeIf { it.isNotBlank() }?.let { out.append(" — livrable : ").append(it.trim()) }
                a.acceptance?.takeIf { it.isNotBlank() }?.let { out.append(" — critère : ").append(it.trim()) }
                out.append("\n")
            }
            out.append("\n")
        }
    }

    private fun minutes(min: Int): String = when {
        min <= 0 -> "0 min"
        min < 60 -> "$min min"
        min % 60 == 0 -> "${min / 60} h"
        else -> "${min / 60} h ${(min % 60).toString().padStart(2, '0')}"
    }
}
